import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import UserContext from "./utils/UserContext";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import TagManager from "react-gtm-module";
import HomeScreen from "./screens/HomeScreen";
import FavouriteScreen from "./screens/FavouriteScreen";
import Loader from "./components/loader";
import Header from "./components/landing_comp/header";
import CourseDetail from "./screens/Courses/CourseDetailScreen";
import TipsTricks from "./screens/TipsTricksScreen";
import Course from "./screens/Courses/CoursesScreen";
import Editprofile from "./screens/EditProfileScreen";
import Quiz from "./screens/QuizScreen";
import { chekUser, generateToken } from "./services/AuthService";
import { decode as base64_decode } from "base-64";
import LandingOTPScreen from "./screens/LandingOTPScreen";
import LandingScreen from "./screens/LandingScreen";
import PrivacyScreen from "./screens/PrivacyScreen";
import LiveQuransScreen from "./screens/LiveQuransScreen";
import "./scss/index.scss";
import "bootstrap/dist/js/bootstrap.bundle";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "primeicons/primeicons.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.css";
import { TermsAndConditions } from "./components/TermsAndConditions";
import Footer from "./components/Footer";
import Faqs from "./components/Faqs";
import SatBanner from "./components/SatCourse/SatNavBanner";
import CoursesScreen from "./screens/SatCourse/CoursesScreen";
import ChaptersScreen from "./screens/SatCourse/ChaptersScreen";
import TopicScreen from "./screens/SatCourse/TopicsScreen";
import VideoPlayerScreen from "./screens/SatCourse/VideoPlayerScreen";
import MockTestScreen from "./screens/SatCourse/MockTestScreen";
import MockInfoScreen from "./screens/SatCourse/MockInfoScreen";
import MockInstructionScreen from "./screens/SatCourse/MockInstructionScreen";
import MOckTestReadingScreen from "./screens/SatCourse/MocKTestReadingScreen";
import MockTestMathematicsScreen from "./screens/SatCourse/MockMathematicsScreen";
import BookMarkScreen from "./screens/SatCourse/BookMarkScreen";
import ContentScreen from "./screens/SatCourse/ContentScreen";
import UrlNames from "./constants/UrlNames";
import { Config } from "./constants/Config";
import {
  clearAllCookies,
  getCookie,
  setCookie,
  getDataFromCookies,
  getQueryParams,
} from "./utils/CommonFunction";

const GUEST_CREDENTIALS = {
  username: Config.GUEST_USERNAME,
  password: Config.GUEST_PASSWORD,
};
/** Delay before re-validating an existing session's user (ms). */
const CHECK_USER_DELAY_MS = 30000;
/** `?parameter=` value that opens the lucky-draw popup. */
const LUCKY_DRAW_PARAM = "10k";
/** Pixel width above which the SAT banner is shown. */
const DESKTOP_BREAKPOINT = 1024;
const DESKTOP_MEDIA_QUERY = `(min-width: ${DESKTOP_BREAKPOINT + 1}px)`;

const App = () => {
  const _location = useLocation();
  const { cookieMsisdn, cookieSubscribeId } = getDataFromCookies();
  const [msisdn, setMsisdn] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [tokenExist, setTokenExist] = useState(false);
  // NOTE: `loader === false` means "show the loading overlay" (legacy semantics).
  const [loader, setLoader] = useState(true);
  const [show, setShow] = useState(false);
  const [contextLoggeIn, setContextloggedIn] = useState(false);
  const [searchParams] = useSearchParams();
  const parameters = searchParams.get("parameter") ?? "";
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia(DESKTOP_MEDIA_QUERY).matches,
  );
  const [satCourseData, setSatCourseData] = useState<unknown[]>([]);
  const [satChapters, setSatChapters] = useState<unknown[]>([]);
  const [checkSatNavbar, setCheckSatNavbar] = useState(false);
  const [checkRefresh, setCheckRefresh] = useState(false);
  const [subscriberId, setSubscriberId] = useState("");

  const checkUserTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Screen components were migrated with differing `token` conventions
  // (`string | null` vs `string | undefined`); `string` is assignable to both.
  // The runtime value (including null) is unchanged — each screen handles
  // nullish `token` per its own typing.
  const screenToken = token as string;

  // Seed identity state from cookies on mount.
  useEffect(() => {
    if (cookieMsisdn) {
      setMsisdn(cookieMsisdn);
    }
    if (cookieSubscribeId) {
      setSubscriberId(cookieSubscribeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track the desktop breakpoint without re-rendering on every resize.
  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const onChange = (event: MediaQueryListEvent): void =>
      setIsDesktop(event.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const loaderStatus = useCallback((status: boolean): void => {
    setLoader(status);
  }, []);

  const handleClose = useCallback((): void => setShow(false), []);

  const updateContext = useCallback((val: boolean): void => {
    setContextloggedIn(val);
  }, []);

  const updateMsisdn = useCallback((val: string): void => {
    setMsisdn(val);
  }, []);

  const fetchCheckUser = useCallback(
    async (userMsisdn: string): Promise<void> => {
      try {
        // The token is injected by the apiClient interceptor (current global.token),
        // so it does not need to be passed explicitly.
        const response = await chekUser(userMsisdn);
        if (response.status === 200 && response.response.desc !== "Active") {
          clearAllCookies();
        }
      } catch (error) {
        // A failed check should not break the session.
      }
    },
    [],
  );

  const initalizeToken = useCallback(async (): Promise<void> => {
    try {
      const data = await generateToken(GUEST_CREDENTIALS);
      if (data.jwttoken) {
        setToken(data.jwttoken);
        global.token = data.jwttoken;
      }
      setTokenExist(true);
    } catch (error) {
      setTokenExist(true);
      setLoader(true);
      console.log("authenticateToken error :", error);
    }
  }, []);

  const checkUser = useCallback((): void => {
    const data = getCookie("data");
    if (data?.msisdn && data?.token) {
      setToken(data.token);
      setTokenExist(true);
      setLoader(false);
      global.token = data.token;
      checkUserTimeout.current = setTimeout(() => {
        fetchCheckUser(data.msisdn);
      }, CHECK_USER_DELAY_MS);
    } else {
      initalizeToken();
    }
    if (parameters === LUCKY_DRAW_PARAM) {
      setShow(true);
    }
  }, [parameters, fetchCheckUser, initalizeToken]);

  const checkParametersFromSubDomain = useCallback(async (): Promise<void> => {
    try {
      let msisdnQuery = getQueryParams("user");
      let tokenQuery = getQueryParams("token");
      let subIdQuery = getQueryParams("subId");
      if (msisdnQuery && tokenQuery && subIdQuery) {
        msisdnQuery = base64_decode(msisdnQuery);
        tokenQuery = base64_decode(tokenQuery);
        subIdQuery = base64_decode(subIdQuery);
        setToken(tokenQuery);
        setMsisdn(msisdnQuery);
        setSubscriberId(subIdQuery);
        setCookie(msisdnQuery, tokenQuery, subIdQuery);
        global.token = tokenQuery;
        setLoader(false);
        setTokenExist(true);
      } else {
        setLoader(false);
        setTokenExist(false);
        checkUser();
      }
    } catch (error) {
      console.log("error in checkParametersFromSubDomain", error);
      initalizeToken();
    }
  }, [checkUser, initalizeToken]);

  // Bootstrap analytics and auth once on mount.
  useEffect(() => {
    TagManager.initialize({ gtmId: Config.GTM_ID });
    checkParametersFromSubDomain();
    return () => {
      if (checkUserTimeout.current) {
        clearTimeout(checkUserTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo(
    () => ({
      contextLoggeIn,
      msisdn,
      updateContext,
      updateMsisdn,
      activeIndex,
      setActiveIndex,
      satCourseData,
      setSatCourseData,
      loader,
      checkSatNavbar,
      setCheckSatNavbar,
      satChapters,
      setSatChapters,
      checkRefresh,
      setCheckRefresh,
      subscriberId,
    }),
    [
      contextLoggeIn,
      msisdn,
      updateContext,
      updateMsisdn,
      activeIndex,
      satCourseData,
      loader,
      checkSatNavbar,
      satChapters,
      checkRefresh,
      subscriberId,
    ],
  );

  return (
    <UserContext.Provider value={contextValue}>
      {loader === false && <Loader />}
      {!tokenExist ? (
        <Loader />
      ) : (
        <>
          {_location.pathname.includes("/landing") ||
          _location.pathname.includes("/phone") ||
          _location.pathname.includes("/privacypolicy") ||
          _location.pathname.includes("/terms") ||
          _location.pathname.includes("/pin") ||
          _location.pathname.includes("/faqs") ? null : (
            <Header
              loader={loader}
              loaderStatus={loaderStatus}
              token={screenToken}
            />
          )}
          {isDesktop ? (
            <>
              {_location.pathname.includes(UrlNames.satCourse) ||
              _location.pathname.includes(UrlNames.chapters) ||
              _location.pathname.includes(UrlNames.topics) ||
              _location.pathname.includes(UrlNames.content) ||
              _location.pathname.includes(UrlNames.mockTest) ||
              _location.pathname.includes(UrlNames.mockInfo) ||
              _location.pathname.includes(UrlNames.mockInsturction) ||
              _location.pathname.includes(UrlNames.mockTestReading) ||
              _location.pathname.includes(UrlNames.mockTestMathematics) ||
              _location.pathname.includes(UrlNames.bookMark) ? (
                <SatBanner />
              ) : (
                ""
              )}
            </>
          ) : (
            ""
          )}

          <Routes>
            <Route path="/*" element={<Navigate to={"/home"} />} />
            <Route
              path="/home"
              element={
                <HomeScreen
                  token={screenToken}
                  loaderStatus={loaderStatus}
                  handleClose={handleClose}
                  show={show}
                />
              }
            />
            <Route
              path="/landing"
              element={
                cookieMsisdn ? (
                  <Navigate replace to={"/home"} />
                ) : (
                  <LandingScreen
                    token={screenToken}
                    loaderStatus={loaderStatus}
                  />
                )
              }
            />

            <Route
              path="/courses"
              element={
                <Course token={screenToken} loaderStatus={loaderStatus} />
              }
            />
            <Route
              path="/tips"
              element={
                <TipsTricks token={screenToken} loaderStatus={loaderStatus} />
              }
            />
            <Route
              path="/favourite"
              element={
                <FavouriteScreen
                  token={screenToken}
                  loaderStatus={loaderStatus}
                />
              }
            />
            <Route
              path="/course_detail"
              element={
                <CourseDetail token={screenToken} loaderStatus={loaderStatus} />
              }
            />
            <Route
              path="/editprofile"
              element={
                <Editprofile token={screenToken} loaderStatus={loaderStatus} />
              }
            />
            <Route
              path="/quiz"
              element={<Quiz token={screenToken} loaderStatus={loaderStatus} />}
            />

            <Route
              path="/pin"
              element={
                cookieMsisdn ? (
                  <Navigate replace to={"/home"} />
                ) : (
                  <LandingOTPScreen
                    token={screenToken}
                    loaderStatus={loaderStatus}
                  />
                )
              }
            />

            <Route
              path="/privacypolicy"
              element={<PrivacyScreen loaderStatus={loaderStatus} />}
            />
            <Route
              path="/terms"
              element={<TermsAndConditions loaderStatus={loaderStatus} />}
            />
            <Route
              path="/faqs"
              element={<Faqs loaderStatus={loaderStatus} />}
            />

            <Route
              path={UrlNames.satCourse}
              element={
                <CoursesScreen
                  token={screenToken}
                  loaderStatus={loaderStatus}
                />
              }
            />
            <Route
              path={UrlNames.chapters}
              element={
                <ChaptersScreen
                  token={screenToken}
                  loaderStatus={loaderStatus}
                />
              }
            />
            <Route
              path={UrlNames.topics}
              element={
                <TopicScreen token={screenToken} loaderStatus={loaderStatus} />
              }
            />
            <Route
              path={UrlNames.videoPlayer}
              element={<VideoPlayerScreen />}
            />
            <Route
              path={UrlNames.bookMark}
              element={
                <BookMarkScreen
                  token={screenToken}
                  loaderStatus={loaderStatus}
                />
              }
            />
            <Route
              path={UrlNames.content}
              element={<ContentScreen token={screenToken} />}
            />
            <Route path={UrlNames.mockTest} element={<MockTestScreen />} />
            <Route path={UrlNames.mockInfo} element={<MockInfoScreen />} />
            <Route
              path={UrlNames.mockInsturction}
              element={<MockInstructionScreen />}
            />
            <Route
              path={UrlNames.mockTestReading}
              element={<MOckTestReadingScreen />}
            />
            <Route
              path={UrlNames.mockTestMathematics}
              element={<MockTestMathematicsScreen />}
            />

            <Route
              path="/livequran"
              element={
                <LiveQuransScreen
                  token={screenToken}
                  loaderStatus={loaderStatus}
                />
              }
            />
            <Route path="*" element={<Navigate replace to={"/home"} />} />
          </Routes>
          {_location.pathname.includes("/landing") ||
          _location.pathname.includes("/terms") ||
          _location.pathname.includes("/privacypolicy") ||
          _location.pathname.includes("/faqs") ||
          _location.pathname.includes("/phone") ||
          _location.pathname.includes("/pin") ? null : (
            <Footer />
          )}
        </>
      )}
    </UserContext.Provider>
  );
};

export default App;
